/**
 * Global Status Color Map
 * ------------------------
 * Single source of truth for every status badge colour across the OPS panel.
 * Changing a colour here updates every page that uses <StatusBadge>.
 *
 * Conventions:
 *  - success / positive outcome → green (emerald)
 *  - in-progress / pending       → amber
 *  - neutral / new / unknown     → slate
 *  - warning / info              → blue
 *  - negative / blocked / hard-fail → red
 *  - soft-fail / deferred        → orange
 *  - enrolled / paid             → green (strongest success)
 */

// ── Canonical semantic tokens ─────────────────────────────────────────────
export const STATUS_TOKENS = {
  success: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  info:    'bg-blue-100 text-blue-700 border-blue-200',
  warning: 'bg-amber-100 text-amber-700 border-amber-200',
  danger:  'bg-red-100 text-red-700 border-red-200',
  soft:    'bg-orange-100 text-orange-700 border-orange-200',
  neutral: 'bg-slate-100 text-slate-700 border-slate-200',
  muted:   'bg-gray-100 text-gray-500 border-gray-200',
};

// ── Map every known status to a semantic token ────────────────────────────
// (Add new statuses here only — never set ad-hoc classes on a badge.)
export const STATUS_MAP = {
  // Lead status
  new:                    STATUS_TOKENS.info,
  wip:                    STATUS_TOKENS.warning,
  contacted:              STATUS_TOKENS.info,
  callback_later:         STATUS_TOKENS.info,
  callback_scheduled:     STATUS_TOKENS.info,
  follow_up:              STATUS_TOKENS.info,
  future_prospect:        STATUS_TOKENS.info,
  dnp:                    STATUS_TOKENS.soft,
  number_not_in_service:  STATUS_TOKENS.muted,
  not_eligible:           STATUS_TOKENS.neutral,
  not_interested:         STATUS_TOKENS.neutral,
  no_show:                STATUS_TOKENS.danger,
  reschedule:             STATUS_TOKENS.soft,
  trial_scheduled:        STATUS_TOKENS.warning,
  trial_completed:        STATUS_TOKENS.success,
  converted:              STATUS_TOKENS.success,
  enrolled:               STATUS_TOKENS.success,

  // Payment status
  created:                STATUS_TOKENS.warning,
  sent:                   STATUS_TOKENS.warning,
  pending:                STATUS_TOKENS.warning,
  paid:                   STATUS_TOKENS.success,
  captured:               STATUS_TOKENS.success,
  failed:                 STATUS_TOKENS.danger,
  expired:                STATUS_TOKENS.neutral,
  refunded:               STATUS_TOKENS.soft,
  partially_refunded:     STATUS_TOKENS.soft,

  // Trial / enrollment
  not_paid:               STATUS_TOKENS.warning,

  // Consultation status
  scheduled:              STATUS_TOKENS.warning,
  confirmed:              STATUS_TOKENS.info,
  completed:              STATUS_TOKENS.success,
  cancelled:              STATUS_TOKENS.neutral,

  // Lead quality
  good:                   STATUS_TOKENS.success,
  average:                STATUS_TOKENS.warning,
  bad:                    STATUS_TOKENS.danger,
};

// ── Lookup helpers ────────────────────────────────────────────────────────
export const getStatusColor = (status) => {
  if (!status) return STATUS_TOKENS.neutral;
  const key = String(status).toLowerCase().trim();
  return STATUS_MAP[key] || STATUS_TOKENS.neutral;
};

export const formatStatusLabel = (status) => {
  if (!status) return 'New';
  return String(status)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};
